import React, { useState, useEffect } from 'react';
import './PortfolioChart.css';

const PortfolioChart = ({ data }) => {
  const [chartData, setChartData] = useState([]);
  const [timeframe, setTimeframe] = useState('1H');
  const [allData, setAllData] = useState([]); // Stocker toutes les données

  // Configuration des timeframes
  const getTimeframeConfig = (tf) => {
    const configs = {
      '15M': { points: 15, interval: 60000, label: '15 minutes' },      // 15 points, 1 min chacun
      '1H': { points: 60, interval: 60000, label: '1 heure' },         // 60 points, 1 min chacun  
      '4H': { points: 48, interval: 300000, label: '4 heures' },       // 48 points, 5 min chacun
      '1D': { points: 24, interval: 3600000, label: '24 heures' }      // 24 points, 1h chacun
    };
    return configs[tf] || configs['1H'];
  };

  // Formater le temps selon le timeframe
  const formatTimeForTimeframe = (date, tf) => {
    switch (tf) {
      case '15M':
      case '1H':
        return date.toLocaleTimeString('fr-FR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case '4H':
        return date.toLocaleTimeString('fr-FR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case '1D':
        return date.toLocaleDateString('fr-FR', { 
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit'
        });
      default:
        return date.toLocaleTimeString('fr-FR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
    }
  };

  // Initialiser avec des zéros si pas de données
  useEffect(() => {
    if (allData.length === 0) {
      const config = getTimeframeConfig(timeframe);
      const initialData = [];
      const now = new Date();
      
      // Créer les points initiaux selon le timeframe
      for (let i = config.points - 1; i >= 0; i--) {
        const time = new Date(now.getTime() - i * config.interval);
        initialData.push({
          timestamp: time.getTime(),
          time: formatTimeForTimeframe(time, timeframe),
          value: data.balance || 0,
          pnl: data.pnl || 0
        });
      }
      
      setAllData(initialData);
      setChartData(initialData);
    }
  }, []);

  // Mettre à jour les données avec les vraies valeurs du bot
  useEffect(() => {
    if (data.balance !== undefined && allData.length > 0) {
      const now = new Date();
      const currentTime = formatTimeForTimeframe(now, timeframe);
      
      setAllData(prev => {
        const newData = [...prev];
        const lastPoint = newData[newData.length - 1];
        
        // Vérifier si on doit ajouter un nouveau point (éviter les doublons)
        const timeDiff = now.getTime() - (lastPoint?.timestamp || 0);
        const config = getTimeframeConfig(timeframe);
        
        if (!lastPoint || timeDiff >= config.interval) {
          // Ajouter un nouveau point
          newData.push({
            timestamp: now.getTime(),
            time: currentTime,
            value: data.balance || 0,
            pnl: data.pnl || 0
          });
          
          // Garder seulement le nombre de points nécessaires
          return newData.slice(-config.points);
        } else {
          // Mettre à jour le dernier point
          newData[newData.length - 1] = {
            ...lastPoint,
            value: data.balance || 0,
            pnl: data.pnl || 0
          };
          return newData;
        }
      });
    }
  }, [data.balance, data.pnl, timeframe]);

  // Mettre à jour l'affichage quand le timeframe change
  useEffect(() => {
    if (allData.length > 0) {
      const config = getTimeframeConfig(timeframe);
      
      // Filtrer et reformater les données selon le nouveau timeframe
      const filteredData = allData.slice(-config.points).map(point => ({
        ...point,
        time: formatTimeForTimeframe(new Date(point.timestamp), timeframe)
      }));
      
      setChartData(filteredData);
    }
  }, [timeframe, allData]);

  const getMinMax = () => {
    if (chartData.length === 0) return { min: 0, max: 100 };
    
    const values = chartData.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1;
    
    return {
      min: Math.max(min - padding, 0),
      max: max + padding
    };
  };

  const { min, max } = getMinMax();
  const range = max - min;

  const generatePath = () => {
    if (chartData.length === 0) return '';
    
    const width = 100; // Pourcentage
    const height = 100; // Pourcentage
    
    const points = chartData.map((point, index) => {
      const x = (index / (chartData.length - 1)) * width;
      const y = height - ((point.value - min) / range) * height;
      return `${x},${y}`;
    });
    
    return `M ${points.join(' L ')}`;
  };

  const getCurrentTrend = () => {
    if (chartData.length < 2) return 'neutral';
    
    const recent = chartData.slice(-5);
    const trend = recent[recent.length - 1].value - recent[0].value;
    
    if (trend > 0) return 'up';
    if (trend < 0) return 'down';
    return 'neutral';
  };

  const trend = getCurrentTrend();

  return (
    <div className="portfolio-chart">
      <div className="chart-header">
        <h3>
          📈 Évolution du Portefeuille 
          <span className={`data-source ${data.balance > 0 ? 'real' : 'zero'}`}>
            {data.balance > 0 ? '🤖 Balance Bot Réelle' : '🔴 Bot Déconnecté'}
          </span>
        </h3>
        <div className="chart-controls">
          <div className="timeframe-selector">
            {['15M', '1H', '4H', '1D'].map(tf => (
              <button
                key={tf}
                className={`timeframe-btn ${timeframe === tf ? 'active' : ''}`}
                onClick={() => setTimeframe(tf)}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Valeur Actuelle:</span>
          <span className="stat-value">
            ${chartData[chartData.length - 1]?.value.toFixed(2) || '0.00'}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Tendance:</span>
          <span className={`stat-value trend-${trend}`}>
            {trend === 'up' && '📈 Hausse'}
            {trend === 'down' && '📉 Baisse'}
            {trend === 'neutral' && '➡️ Stable'}
          </span>
        </div>
      </div>

      <div className="chart-container">
        <svg className="chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Grille de fond */}
          <defs>
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
            </pattern>
            <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(0, 212, 255, 0.3)" />
              <stop offset="100%" stopColor="rgba(0, 212, 255, 0.05)" />
            </linearGradient>
          </defs>
          
          <rect width="100" height="100" fill="url(#grid)" />
          
          {/* Zone sous la courbe */}
          {chartData.length > 0 && (
            <path
              d={`${generatePath()} L 100,100 L 0,100 Z`}
              fill="url(#chartGradient)"
            />
          )}
          
          {/* Ligne de la courbe */}
          {chartData.length > 0 && (
            <path
              d={generatePath()}
              fill="none"
              stroke="#00d4ff"
              strokeWidth="0.5"
              className="chart-line"
            />
          )}
          
          {/* Points sur la courbe */}
          {chartData.map((point, index) => {
            if (index % 10 !== 0 && index !== chartData.length - 1) return null;
            
            const x = (index / (chartData.length - 1)) * 100;
            const y = 100 - ((point.value - min) / range) * 100;
            
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="0.8"
                fill="#00d4ff"
                className="chart-point"
              />
            );
          })}
        </svg>
        
        {chartData.length === 0 && (
          <div className="no-data">
            <span>📊</span>
            <p>Données en cours de chargement...</p>
          </div>
        )}
      </div>

      <div className="chart-footer">
        <div className="time-labels">
          <span>{chartData[0]?.time || '--:--'}</span>
          <span>Maintenant</span>
        </div>
      </div>
    </div>
  );
};

export default PortfolioChart;
